import { useId, useState } from 'react';
import { SiteLink } from './RedesignShell';
import { geographyShapes, mapHeight as height, mapWidth as width, projectPoint } from './geographyShapes';
import './geography-map.css';

const deliveryProject = {
  name: 'Приложение для доставки',
  work: 'Магазины и рестораны подключаются к приложению, а пользователи находят их и заказывают доставку.',
};

const locations = [
  { id: 'moscow', name: 'Москва', label: 'Москва · HQ', coordinates: [37.62, 55.75], detail: 'Штаб-квартира Fatality', cases: [
    { name: 'TelStore', work: 'Интернет-магазин, CRM, CMS и SEO', href: '/portfolio/tech-store/' },
    { name: 'Elite', work: 'Сайт, SEO и ведение Яндекс Карт', href: '/portfolio/sokolniki-restaurant/' },
    { name: 'НСО', work: 'Строительная компания' },
    { name: 'Оптовый магазин техники', work: 'Автоматизация бизнес-процессов' },
  ] },
  { id: 'spb', name: 'Санкт-Петербург', label: 'СПб', coordinates: [30.32, 59.94], cases: [
    { name: 'SmileOnline', work: 'Оформление контента для Яндекс Карт' },
  ] },
  { id: 'ulyanovsk', name: 'Ульяновск', label: 'Ульяновск', coordinates: [48.4, 54.31], cases: [
    { name: 'Стоматология «АРТ»' },
  ] },
  { id: 'obninsk', name: 'Обнинск', label: 'Обнинск', coordinates: [36.61, 55.09], cases: [
    { name: 'Магазин техники и электроники', work: 'Логотип и брендинг' },
  ] },
  { id: 'khmao', name: 'Ханты-Мансийский автономный округ', label: 'ХМАО', coordinates: [72, 61.2], cases: [deliveryProject] },
  { id: 'kazakhstan', name: 'Казахстан', label: 'Казахстан', coordinates: [67, 48], cases: [
    { name: 'TL Логистик', work: 'Приложение для грузоперевозок: компании ищут водителей фур, водители — заказы.' },
    deliveryProject,
    { name: 'Приложение для индивидуального пошива', work: 'Сканирует параметры тела для заказа одежды по меркам. Дизайн можно выбрать в каталоге или придумать с помощью ИИ.' },
  ] },
  { id: 'baku', name: 'Азербайджан', label: 'Азербайджан', coordinates: [49.87, 40.41], cases: [
    { name: 'Онлайн-чаевые для ресторанов', work: 'Стартап: сервис онлайн-чаевых для ресторанов' },
  ] },
  { id: 'abkhazia', name: 'Абхазия', label: 'Абхазия', coordinates: [40.82, 43.09], detail: 'Новый Афон', cases: [
    { name: 'Saint Afon Hotel', work: 'Брендинг, сайт, CRM, CMS, SEO и Яндекс Карты' },
  ] },
].map((location) => ({ ...location, ...projectPoint(...location.coordinates) }));

const hub = locations[0];

function routeTo({ x, y }) {
  return `M${hub.x} ${hub.y} Q${(hub.x + x) / 2 + (y > 210 ? 36 : 0)} ${Math.min(hub.y, y) - 35} ${x} ${y}`;
}

function ProjectRow({ name, work, href }) {
  const content = <><strong>{name}</strong>{work ? <span>{work}</span> : null}</>;
  return (
    <li className="geo-atlas__project">
      {href ? (
        <SiteLink className="geo-atlas__project-body" href={href}>
          {content}<span className="geo-atlas__project-arrow" aria-hidden="true">→</span>
        </SiteLink>
      ) : <div className="geo-atlas__project-body">{content}</div>}
    </li>
  );
}

export default function GeographyMap() {
  const [selectedId, setSelectedId] = useState(hub.id);
  const selected = locations.find(({ id }) => id === selectedId);
  const id = useId();
  const detailsId = `${id}-details`;

  return (
    <div className="geo-atlas" role="group" aria-label="География клиентов и проектов">
      <div className="geo-atlas__map">
        <div className="geo-atlas__top">
          <span>Клиенты и проекты</span>
          <span>Natural Earth · фрагмент карты</span>
        </div>
        <div className="geo-atlas__stage" style={{ aspectRatio: `${width} / ${height}` }}>
          <svg viewBox={`0 0 ${width} ${height}`} aria-hidden="true" focusable="false">
            {geographyShapes.map(({ code, d, featured, disputed }) => (
              <path key={code} d={d} fillRule="evenodd" className={`geo-atlas__territory${featured ? ' is-featured' : ''}${disputed ? ' is-disputed' : ''}`} />
            ))}
            <text x="298" y="64" className="geo-atlas__country">РОССИЯ</text>
            {locations.slice(1).map((location) => (
              <path key={location.id} d={routeTo(location)} className={`geo-atlas__route${location.id === selectedId ? ' is-active' : ''}`} />
            ))}
          </svg>
          {locations.map(({ id: locationId, name, label, x, y }) => (
            <button
              type="button"
              key={locationId}
              className={`geo-atlas__pin geo-atlas__pin--${locationId}`}
              style={{ left: `${x / width * 100}%`, top: `${y / height * 100}%` }}
              aria-label={name}
              aria-pressed={locationId === selectedId}
              aria-controls={detailsId}
              onClick={() => setSelectedId(locationId)}
            >
              <span className="geo-atlas__dot" aria-hidden="true" />
              <span className="geo-atlas__tag" aria-hidden="true">{label}</span>
            </button>
          ))}
        </div>
        <div className="geo-atlas__locations" role="group" aria-label="Выбор локации">
          {locations.map(({ id: locationId, name, label }) => (
            <button
              type="button"
              key={locationId}
              aria-label={name}
              aria-pressed={locationId === selectedId}
              aria-controls={detailsId}
              onClick={() => setSelectedId(locationId)}
            >{locationId === hub.id ? name : label}</button>
          ))}
        </div>
      </div>
      <div className="geo-atlas__details">
        <div className="geo-atlas__case-head">
          <div className="geo-atlas__location-head" aria-live="polite">
            <strong>{selected.name}</strong>
            {selected.detail ? <span>{selected.detail}</span> : null}
          </div>
          <SiteLink className="geo-atlas__more" href="/portfolio/">
            Смотреть все кейсы <span aria-hidden="true">→</span>
          </SiteLink>
        </div>
        <div id={detailsId} aria-live="polite">
          {selected.cases ? (
            <ul className="geo-atlas__cases" aria-label="Проекты">
              {selected.cases.map((project) => <ProjectRow key={project.name} {...project} />)}
            </ul>
          ) : <p className="geo-atlas__empty">Публичных кейсов пока нет.</p>}
        </div>
      </div>
    </div>
  );
}
